import { memo } from 'react';

import { CheckCheck, X } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';

import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import type { BooleanView } from '@/components/ui/widget/views/boolean/boolean.view.type';
import type { TextView } from '@/components/ui/widget/views/text/text.view.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { useWidgetRenderContext } from '@/components/ui/widget/widgetRenderContext';
import { getByPath } from '@/utils/widgets';

type Props = {
  view: BooleanView;
  data: WidgetData;
};

const BooleanViewImpl = memo(({ view, data }: Props) => {
  const { path, isColored = true } = view;
  const renderContext = useWidgetRenderContext();

  return (
    <div
      className={cn(
        'flex w-full items-center justify-start text-center',
        renderContext === 'table-cell' && 'justify-center'
      )}
    >
      {getByPath(data, path) ? (
        <CheckCheck
          className={cn('text-muted-foreground', isColored && 'text-green-700')}
          size={24}
        />
      ) : (
        <X
          className={cn('text-muted-foreground', isColored && 'text-red-600')}
          size={24}
        />
      )}
    </div>
  );
});

// sort defaults to text on path
const getSortConfig = (view: BooleanView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: BooleanView): TextView => ({
  type: 'text',
  path: view.path
});

// width defaults to 'small'
const getDefaultWidth = (): ColumnWidth => 'small' as const;

// stringifies data at path
const getStringifyFunction = (view: BooleanView) => {
  const { path } = view;
  if (path !== undefined)
    return (data: WidgetData) => (!!getByPath(data, path)).toString();
  return () => '';
};

export const booleanViewProvider = {
  component: BooleanViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
